import CustomButton from "@components/common/Button";
import LoadingSpinner from "@components/common/LoadingSpinner";
import NotFound from "@components/common/NotFound";
import { notify } from "@components/common/Notification";
import { checkFormChanges } from "@lib/checkFormChanges";
import { filterByLabel } from "@lib/filterByLabel";
import SocialConfigurationService from "@/services/APIs/SocialConfigurationService";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Form, Input, Select } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const SocialConfigurationPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);

  const providerOptions = [
    { value: "google", label: "Google" },
    { value: "facebook", label: "Facebook" },
    { value: "twitter", label: "Twitter" },
    { value: "linkedin", label: "LinkedIn" },
    { value: "instagram", label: "Instagram" },
    { value: "apple", label: "Apple" },
    { value: "github", label: "GitHub" },
    { value: "custom", label: "Custom" },
  ];

  // Use React Query for mutations
  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);

    if (isEditMode) {
      // Check for changes before updating
      if (!checkFormChanges(values, socialConfiguration ?? {}, notify)) {
        setIsSubmitting(false);
        return;
      }

      const response =
        await SocialConfigurationService.updateSocialConfiguration(
          id ?? "",
          values
        );
      if (response) {
        navigate(navigationRoutes.SOCIAL_CONFIGURATION);
      }
    } else {
      const response =
        await SocialConfigurationService.createSocialConfiguration(values);
      if (response) {
        navigate(navigationRoutes.SOCIAL_CONFIGURATION);
      }
    }
    setIsSubmitting(false);
  };

  // Use React Query for data fetching
  const { data: socialConfiguration, isLoading } = useQuery({
    queryKey: ["socialConfiguration", id],
    queryFn: () =>
      SocialConfigurationService.getSocialConfigurationById(id ?? ""),
    enabled: !!id,
  });

  useEffect(() => {
    if (isEditMode && socialConfiguration) {
      form.setFieldsValue(socialConfiguration);
    }
  }, [isEditMode, socialConfiguration, form]);

  useEffect(() => {
    if (selectedProvider) {
      const apiBase = import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? "";
      const redirectUrl = apiBase
        ? `${apiBase.replace(/\/$/, "")}/auth/${selectedProvider}/callback/`
        : "";
      form.setFieldsValue({ redirect_url: redirectUrl });
    }
  }, [selectedProvider, form]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isEditMode && !socialConfiguration) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={
            isEditMode
              ? "Edit Social Configuration"
              : "Add Social Configuration"
          }
          onBack={() => navigate(navigationRoutes.SOCIAL_CONFIGURATION)}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={
                isEditMode
                  ? "Save Social Configuration Changes"
                  : "Save New Social Configuration"
              }
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="grid grid-cols-2 gap-4">
          {/* New Fields */}
          <Form.Item
            name="provider"
            label="Provider"
            rules={[{ required: true, message: "Please enter provider" }]}
          >
            <Select
              options={providerOptions?.map((provider: any) => ({
                value: provider.value,
                label: provider.label,
              }))}
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Select Provider"
              onChange={value => {
                setSelectedProvider(value);
              }}
              value={selectedProvider}
              allowClear
              loading={false}
              filterOption={filterByLabel}
            />
          </Form.Item>

          <Form.Item name="client_id" label="Client ID">
            <Input placeholder="Enter client id" />
          </Form.Item>

          <Form.Item name="client_secret" label="Client Secret">
            <Input.Password placeholder="Enter client secret" />
          </Form.Item>

          <Form.Item name="redirect_url" label="Redirect URL">
            <Input placeholder="Enter redirect url" readOnly disabled />
          </Form.Item>

          <Form.Item name="scope" label="Scope">
            <Input placeholder="Enter scope" />
          </Form.Item>

          <Form.Item name="auth_params" label="Auth Params">
            <Input placeholder="Enter auth params" />
          </Form.Item>
        </div>
      </Form>
    </Card>
  );
};

export default SocialConfigurationPage;
